import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import type { Color } from '../../engine/geo/mesh';
import type { RGB } from '../../engine/render/Renderer';

export type BiomeId = 'hearth' | 'marsh' | 'dunes' | 'ironwood' | 'cistern';

export interface BiomeInfo {
  id: BiomeId;
  center: Vec3;
  radius: number;
  /** XZ region the biome's heightfield covers (regions never overlap). */
  region: AABB;
  name: string;
}

/** World is a hub with three surface spokes; north is -Z. The Cistern is a separate region far away. */
export const BIOMES: Record<BiomeId, BiomeInfo> = {
  hearth: { id: 'hearth', name: 'Hearth Meadow', center: new Vec3(0, 0, 0), radius: 68, region: AABB.fromMinMax(-100, -1000, -100, 100, 1000, 100) },
  marsh: { id: 'marsh', name: 'Sallow Marsh', center: new Vec3(-200, 0, 0), radius: 68, region: AABB.fromMinMax(-300, -1000, -100, -100, 1000, 100) },
  dunes: { id: 'dunes', name: 'Glass Dunes', center: new Vec3(0, 0, -200), radius: 68, region: AABB.fromMinMax(-100, -1000, -300, 100, 1000, -100) },
  ironwood: { id: 'ironwood', name: 'Ironwood', center: new Vec3(200, 0, 0), radius: 68, region: AABB.fromMinMax(100, -1000, -100, 300, 1000, 100) },
  cistern: { id: 'cistern', name: 'The Cistern', center: new Vec3(0, 0, 600), radius: 70, region: AABB.fromMinMax(-100, -1000, 500, 100, 1000, 700) },
};

export interface Corridor { a: Vec3; b: Vec3; width: number; from: BiomeId; to: BiomeId }
export const CORRIDORS: Corridor[] = [
  { a: new Vec3(-62, 0, 0), b: new Vec3(-138, 0, 0), width: 9, from: 'hearth', to: 'marsh' },
  { a: new Vec3(0, 0, -62), b: new Vec3(0, 0, -138), width: 9, from: 'hearth', to: 'dunes' },
  { a: new Vec3(62, 0, 0), b: new Vec3(138, 0, 0), width: 9, from: 'hearth', to: 'ironwood' },
];

export function biomeAt(x: number, z: number): BiomeId {
  for (const b of Object.values(BIOMES)) if (x >= b.region.min.x && x <= b.region.max.x && z >= b.region.min.z && z <= b.region.max.z) return b.id;
  return 'hearth';
}

/** Distance from (x,z) to segment ab in XZ. */
export function segDist(x: number, z: number, a: Vec3, b: Vec3): number {
  const dx = b.x - a.x, dz = b.z - a.z;
  const l2 = dx * dx + dz * dz;
  let t = l2 > 0 ? ((x - a.x) * dx + (z - a.z) * dz) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(x - (a.x + dx * t), z - (a.z + dz * t));
}

/** Five colors per biome (§10). Index: 0 ground, 1 ground alt, 2 prop dark, 3 prop light, 4 accent. */
export const PALETTE: Record<BiomeId, [Color, Color, Color, Color, Color]> = {
  hearth: [[0.45, 0.62, 0.28], [0.58, 0.7, 0.3], [0.36, 0.28, 0.2], [0.82, 0.76, 0.62], [0.95, 0.72, 0.3]],
  marsh: [[0.22, 0.34, 0.28], [0.3, 0.42, 0.3], [0.16, 0.2, 0.18], [0.5, 0.58, 0.44], [0.35, 0.95, 0.75]],
  dunes: [[0.96, 0.9, 0.7], [0.9, 0.78, 0.5], [0.42, 0.34, 0.26], [0.99, 0.97, 0.9], [0.3, 0.85, 0.95]],
  ironwood: [[0.1, 0.12, 0.18], [0.16, 0.2, 0.24], [0.08, 0.07, 0.1], [0.55, 0.5, 0.62], [0.9, 0.25, 0.2]],
  cistern: [[0.26, 0.27, 0.3], [0.2, 0.21, 0.24], [0.06, 0.06, 0.08], [0.36, 0.38, 0.42], [0.7, 0.4, 0.95]],
};

export interface Atmosphere {
  sunDir: Vec3; sunColor: RGB; skyColor: RGB; groundColor: RGB; fogColor: RGB; fogNear: number; fogFar: number;
  skyTop: RGB; skyHorizon: RGB; skyBottom: RGB; sunDisc: number; vignette: number; heat: number; outline: number; shadows: boolean; ambientMul: number;
}

export const ATMOS: Record<BiomeId, Atmosphere> = {
  hearth: {
    sunDir: new Vec3(0.55, 0.42, 0.45).normalize(), sunColor: [0.78, 0.68, 0.52], skyColor: [0.42, 0.46, 0.56], groundColor: [0.24, 0.22, 0.14],
    fogColor: [0.93, 0.82, 0.68], fogNear: 55, fogFar: 150, skyTop: [0.5, 0.68, 0.92], skyHorizon: [0.98, 0.8, 0.62], skyBottom: [0.6, 0.5, 0.42], sunDisc: 1, vignette: 0.5, heat: 0, outline: 0.9, shadows: true, ambientMul: 1,
  },
  marsh: {
    sunDir: new Vec3(0.2, 0.8, -0.3).normalize(), sunColor: [0.35, 0.42, 0.38], skyColor: [0.3, 0.42, 0.38], groundColor: [0.1, 0.15, 0.13],
    fogColor: [0.24, 0.4, 0.38], fogNear: 8, fogFar: 42, skyTop: [0.2, 0.32, 0.3], skyHorizon: [0.28, 0.42, 0.38], skyBottom: [0.15, 0.22, 0.2], sunDisc: 0, vignette: 0.8, heat: 0, outline: 0.7, shadows: true, ambientMul: 1.1,
  },
  dunes: {
    sunDir: new Vec3(0.3, 0.9, 0.25).normalize(), sunColor: [0.85, 0.82, 0.74], skyColor: [0.4, 0.62, 0.72], groundColor: [0.42, 0.36, 0.24],
    fogColor: [0.92, 0.9, 0.78], fogNear: 60, fogFar: 200, skyTop: [0.15, 0.6, 0.85], skyHorizon: [0.85, 0.95, 0.95], skyBottom: [0.7, 0.6, 0.4], sunDisc: 1.4, vignette: 0.4, heat: 1, outline: 0.9, shadows: true, ambientMul: 1,
  },
  ironwood: {
    sunDir: new Vec3(0.12, 0.95, 0.1).normalize(), sunColor: [0.7, 0.66, 0.5], skyColor: [0.2, 0.18, 0.3], groundColor: [0.05, 0.05, 0.08],
    fogColor: [0.09, 0.09, 0.15], fogNear: 14, fogFar: 70, skyTop: [0.12, 0.1, 0.2], skyHorizon: [0.3, 0.22, 0.38], skyBottom: [0.06, 0.06, 0.1], sunDisc: 0, vignette: 0.8, heat: 0, outline: 0.7, shadows: true, ambientMul: 0.9,
  },
  cistern: {
    sunDir: new Vec3(0, 1, 0), sunColor: [0, 0, 0], skyColor: [0.08, 0.09, 0.16], groundColor: [0.02, 0.02, 0.03], fogColor: [0.0, 0.0, 0.01],
    fogNear: 4, fogFar: 26, skyTop: [0, 0, 0], skyHorizon: [0, 0, 0.01], skyBottom: [0, 0, 0], sunDisc: 0, vignette: 1.1, heat: 0, outline: 0.5, shadows: false, ambientMul: 0.5,
  },
};

/** Key spots used by several systems. */
export const SPOTS = {
  goldKeep: new Vec3(0, 0, -6),
  goldKeepGate: new Vec3(0, 0, 3.5),
  spawn: new Vec3(0, 0, 14),
  stoneCircle: new Vec3(24, 0, 24),
  swordSpot: new Vec3(24, 0, 24),
  // marsh
  marshNearIsland: new Vec3(-196, 0, 22),
  marshFarIsland: new Vec3(-232, 0, -26),
  marshChannel: new Vec3(-218, 0, -4),
  marshShrine: new Vec3(-238, 0, -32),
  // dunes
  obelisk: new Vec3(8, 0, -226),
  whiteKeyBuried: new Vec3(-34, 0, -190),
  // ironwood
  whiteKeep: new Vec3(200, 0, 0),
  well: new Vec3(200, 0, -19),
  ravine: new Vec3(176, 0, -34),
  // cistern (galleries form a loop: A top z=560, B east x=48, D bottom z=662, C west x=-40 flooded)
  cisternEntry: new Vec3(-40, 0, 560),
  cisternChasm: new Vec3(-21, 0, 560),
  blackKeep: new Vec3(0, 0, 640),
  greyDotNiche: new Vec3(-40, 0, 600),
  wyrmHome: new Vec3(-40, 0, 634),
};

/** Cistern corridors as XZ rectangles [minX, minZ, maxX, maxZ]; everything else is stone. */
export const CISTERN_CORRIDORS: [number, number, number, number][] = [
  [-44, 556, 52, 564],   // A: top
  [44, 556, 52, 666],    // B: east
  [-44, 658, 52, 666],   // D: bottom
  [-44, 556, -36, 666],  // C: west (flooded past z≈598)
  [-6, 646, 6, 660],     // keep forecourt
  [-11, 633, 11, 647],   // Black Keep interior (so gallery walls stay out of it)
];
export const CISTERN_CEILING = 6.5;
